import { Environment } from 'src/Environment';

import { request } from './request';
import { getUserId, getMerchantId } from './user.service';
import { parseObjectToQueryParams } from 'src/helpers/functional-helpers';
import { HandleTechnicalError } from 'src/context/technical-error-context';
import { Api } from 'src/common';
import { TerminalTransaction } from 'src/common/types';
import { DateTime } from 'luxon';
import { authenticate } from './auth-service';

type Sort = { empty: boolean; unsorted: boolean; sorted: boolean };

type Pageable = {
  offset: number;
  pageNumber: number;
  pageSize: number;
  paged: boolean;
  sort: Sort;
  unpaged: boolean;
};

export type RetrievedRefund = {
  terminalId: string | undefined;
  retrievalReferenceNumber: string;
  methodOfPayment: string;
  accountType: string;
  currencyCode: string;
  PAN: string;
};

type SearchRefundsResponse = {
  result: SearchRefundsResult;
};

type SearchRefundsV2Response = {
  content: TerminalTransaction[];
  empty: boolean;
  first: boolean;
  last: boolean;
  number: number;
  numberOfElements: number;
  pageable: Pageable;
  size: number;
  sort: Sort;
};

type SearchRefundsResult = {
  Rows: TerminalTransaction[];
  NumberOfPages: number;
};

type GetRefundDetailsResponse = {
  result: {
    data: Omit<RetrievedRefund, 'terminalId' | 'pan'>;
  };
};

// if this doesn't work add the details to sessionstorage rather
const retrievedRefunds: RetrievedRefund[] = [];

export const notificationMap = {
  email: '03',
  sms: '01',
};

interface IRefundTransactionsService {
  searchRefunds(
    abortController: AbortController,
    fromDate: string,
    toDate: string,
    reference: string | null,
    fromAmount: string | null,
    toAmount: string | null,
    pageNumber?: number,
    handleErrorResponse?: HandleTechnicalError,
  ): Promise<SearchRefundsResult>;
  searchRefundsV2(
    dateFrom: Date,
    dateTo: Date,
    pageSize?: number,
    pageNumber?: number,
    abortController?: AbortController,
  ): Promise<SearchRefundsResult>;
  getRefundDetails(
    abortController: AbortController,
    recordId: string | undefined,
    terminalId: string | undefined,
    pan: string,
    handleErrorResponse: HandleTechnicalError,
  ): Promise<RetrievedRefund>;
  submitRefund(
    amount: number,
    notificationType: string,
    notificationAddress: string | undefined,
    recordId: string | undefined,
    handleErrorResponse: HandleTechnicalError,
    methodOfPayment: string | undefined,
    refundDetails: RetrievedRefund | undefined,
  ): Promise<{}>;
}

class RefundTransactionsService implements IRefundTransactionsService {
  private SEARCH_REFUNDS_ENDPOINT = 'merchant/refunds';
  private REFUND_TRANSACTIONS_ENDPOINT_V2 = 'refund-transactions';
  private SUBMIT_REFUND_ENDPOINT = 'merchant/refunds/edit';
  private REFUND_DETAILS_ENDPOINT = 'merchant/refunds/retrieve';

  async searchRefunds(
    abortController: AbortController,
    fromDate: string,
    toDate: string,
    reference: string | null,
    fromAmount: string | null, // from and to amount are treated as cents by traderoot
    toAmount: string | null,
    pageNumber: number = 1,
    handleErrorResponse?: HandleTechnicalError,
  ): Promise<SearchRefundsResult> {
    const userId = getUserId();
    const merchantId = getMerchantId();

    const params = {
      userReference: userId,
      merchantID: merchantId,
      localTransactionFromDate: fromDate,
      localTransactionToDate: toDate,
      retrievalReference: reference,
      transactionFromAmount: fromAmount ? Math.round(parseFloat(fromAmount) * 100) : undefined,
      transactionToAmount: toAmount ? Math.round(parseFloat(toAmount) * 100) : toAmount,
      pageNumber: !Environment.current?.disabledFeatures?.pagination ? pageNumber : undefined,
    };

    const { status, response } = await Api.get<SearchRefundsResponse>(
      `${this.SEARCH_REFUNDS_ENDPOINT}?${parseObjectToQueryParams(params)}`,
      {
        signal: abortController?.signal || null,
      },
    );

    const isAborted = !!abortController && abortController.signal.aborted;
    if (status === 200) {
      return response.result;
    } else if (handleErrorResponse && !isAborted) {
      handleErrorResponse('error response from the merchant/refunds GET endpoint', false, false);
    }

    throw new Error('Technical difficulties');
  }

  async searchRefundsV2(
    dateFrom: Date,
    dateTo: Date,
    pageSize?: number,
    pageNumber?: number,
    abortController?: AbortController,
  ): Promise<SearchRefundsResult> {
    const accessToken = await authenticate();

    if (!accessToken) {
      throw new Error('Refund transactions: unable to authenticate');
    }

    const merchantId = getMerchantId();

    const params = {
      accountType: 'MERCHANT',
      merchantID: merchantId,
      dateFrom: DateTime.fromJSDate(dateFrom).toFormat('yyyy-MM-dd'),
      dateTo: DateTime.fromJSDate(dateTo).toFormat('yyyy-MM-dd'),
      pageSize: pageSize,
      pageNumber: pageNumber,
    };

    const queryString = parseObjectToQueryParams(params);
    const queryUrl = `${this.REFUND_TRANSACTIONS_ENDPOINT_V2}?${queryString}`;

    try {
      const { status, response } = await Api.getPrivateApiV2<SearchRefundsV2Response>(queryUrl, {
        signal: abortController?.signal,
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      // const isAborted = !!abortController && abortController.signal.aborted;
      if (status === 200) {
        return { Rows: response?.content, NumberOfPages: response.number };
      } else {
        throw Error(
          `Refund transactions: error response ${queryUrl} GET endpoint: response code: ${status}, response: ${JSON.stringify(
            response,
          )}`,
        );
      }
    } catch (error) {
      console.error(error);
      throw Error(
        "Uh oh! We're experiencing technical difficulties, but we're working on it. Please try again later.",
      );
    }
  }

  async getRefundDetails(
    abortController: AbortController,
    recordId: string | undefined,
    terminalId: string | undefined,
    pan: string,
    handleErrorResponse: HandleTechnicalError,
  ): Promise<RetrievedRefund> {
    const userId = getUserId();

    const params = {
      userReference: userId,
      id: recordId,
    };

    try {
      const { status, response } = await Api.get<GetRefundDetailsResponse>(
        `${this.REFUND_DETAILS_ENDPOINT}?${parseObjectToQueryParams(params)}`,
        { signal: abortController?.signal || null },
      );

      const { result } = response;

      const isAborted = !!abortController && abortController.signal.aborted;
      if (status === 200) {
        const data = { ...result.data, terminalId: terminalId, PAN: pan };
        retrievedRefunds.push(data);
        return data;
      }

      if (handleErrorResponse && !isAborted) {
        handleErrorResponse(
          'error response from the merchant/refunds/retrieve GET endpoint',
          true,
          false,
        );
      }

      throw new Error('Technical difficulties');
    } catch (error) {
      if (error instanceof Error) {
        console.error(error.message);
      }
      throw new Error(`Technical difficulties: ${error}`);
    }
  }

  async submitRefund(
    amount: number,
    notificationType: string,
    notificationAddress: string | undefined,
    recordId: string | undefined,
    handleErrorResponse: HandleTechnicalError,
    methodOfPayment: string | undefined,
    refundDetails: RetrievedRefund | undefined,
  ) {
    const userId = getUserId();
    const merchantId = getMerchantId();
    const cachedRefundDetails = !!refundDetails ? refundDetails : retrievedRefunds[recordId ?? 0];

    const res = await request(
      this.SUBMIT_REFUND_ENDPOINT,
      {
        fetchOptions: {
          method: 'POST',
          body: JSON.stringify({
            userReference: userId,
            recordId: recordId,
            merchantID: merchantId,
            terminalID: cachedRefundDetails.terminalId,
            retrievalReference: cachedRefundDetails.retrievalReferenceNumber,
            methodOfPayment: cachedRefundDetails.methodOfPayment || methodOfPayment,
            accountType: cachedRefundDetails.accountType,
            currencyCode: cachedRefundDetails.currencyCode,
            transactionAmount: `${amount}`,
            pan: cachedRefundDetails.PAN,
            notificationAddress: notificationAddress,
            notificationType: notificationType,
            uuid: recordId, // this is not a required field on TR but DXL requires it
          }),
        },
      },
      null,
      null, // TealiumEvents.dashboardError,
      true,
    );

    if (handleErrorResponse && res?.status >= 500) {
      handleErrorResponse(
        'error response from the merchant/refunds/edit POST endpoint',
        true,
        false,
      );
    }

    return {
      ok: res?.status === 200,
      result: res?.status === 200 ? res?.result : null,
      error: res?.status >= 300 ? res?.result : null,
    };
  }
}

const refundTransactionsService = new RefundTransactionsService();
export { refundTransactionsService };
